import { Analytics } from '@util/Analytics';
import React, { ReactElement } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import {
  getCoverageConsentsUserData,
  getCoverageConsentTicks,
  getOfferedCoverageConsents,
} from '../../../util/InsuranceCoverageCheck';
import { NavigationProps } from '../FlowNavigation';
import {
  CoverageConsentsLayout,
  CoverageConsentsLayoutConfig,
  CoverageConsentTicks,
} from '../layouts/CoverageConsentsLayout';

export type CoverageConsentsScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.CoverageConsents,
  CoverageConsentsLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  CoverageConsentsScreenConfig<ScreenNames>,
  ScreenNames
>;

/**
 * Every coverage consent on one screen, after the user's details and right before the result. All
 * optional: Continue works with nothing ticked, and the check is only filed if the user asked us to
 * contact their health plan or employer (`isCoverageCheckComplete`).
 */
export const CoverageConsentsScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { layout, navigation } = props;
  const consents = useAppStore((state) => getOfferedCoverageConsents(state, layout));
  const prefill = useAppStore((state) => getCoverageConsentTicks(state.userData));

  function onSubmit(ticks: CoverageConsentTicks): void {
    const userData = getCoverageConsentsUserData(
      consents.map(({ consent }) => consent),
      ticks
    );
    dispatchUserData(userData);
    Object.entries(userData).forEach(([question, [answer]]) =>
      Analytics.track({ event: Analytics.Event.o_question_answered, question, answer })
    );
    navigation.next(props);
  }

  return (
    <CoverageConsentsLayout
      {...layout}
      consents={consents}
      prefill={prefill}
      onPressPrimaryButton={onSubmit}
    />
  );
};
